import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavBar, Tabs, Popup, Toast, Skeleton } from 'antd-mobile';
import { DownOutline, UpOutline, RightOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { getNavBarContainerStyle } from '../../../utils/deviceUtils';
import PullToRefreshWrapper from '../../../components/PullToRefreshWrapper';
import MapboxMap from '../../../components/Map/MapboxMapView';
import DspParcelDetailCard, { DspParcelDetailCardRef } from './components/DspParcelDetailCard';
import { GET_DELIVERY_TASK_DETAIL, GET_ALL_EXCEPTION_DICTIONARIES_FULL } from '../graphql/queries/getDeliveryTasks';
import { getExceptionTypeText } from '../../../utils/i18nUtils';
import { ReactComponent as DriverSvg } from '../icons/svgs/driver.svg';
import { DspPath } from '../../../enums/DeliveryNavigatePathEnum';
import { formatStopNumber } from '../../../utils/stopNumberUtils';
import {BarCodeScanner} from "../../../components/BarCodeScanner";
import {useNetwork} from "../../../hooks/useNetwork";
import DspSearchBar from "../components/DspSearchBar";
import { ParcelStatus, ExceptionDictionaryType, Parcel, TaskDetail } from '../graphql/types/DeliveryTaskType';
import { MapBoxPageMarker } from '../../../constants/mapBoxTrackLog';
import {
    closeNavigationModals as closeNavigationModalsUtil,
    emitCloseNavigationEvent,
    forceCloseNavigationModal
} from '../../../utils/navigationUtils';

const DspDeliveryTaskDetailsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {isOnline} = useNetwork();
    const taskId = location.state?.taskId;
    const activeTabFromState = location.state?.activeTab;

    // GraphQL 查询
    const { data, loading, error, refetch } = useQuery(GET_DELIVERY_TASK_DETAIL, {
        variables: { taskId: parseInt(taskId) },
        skip: !taskId,
        fetchPolicy: 'network-only'
    });

    // 查询异常字典数据
    const { data: exceptionDictData } = useQuery(GET_ALL_EXCEPTION_DICTIONARIES_FULL, {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all'
    });

    // 状态管理
    const taskDetail = useMemo(() => {
        if (!data?.getDeliveryTaskDetail) return null;

        const task = data.getDeliveryTaskDetail;
        const parcels = task.parcels || [];
        const exceptionDictionaries = exceptionDictData?.getAllExceptionDictionariesFull || [];

        // 计算各种统计数据
        const deliveringCount = parcels.filter((p: any) => p.status === ParcelStatus.DELIVERY_IN_PROGRESS).length;
        const deliveredCount = parcels.filter((p: any) => p.status === ParcelStatus.DELIVERED).length;
        const failedCount = parcels.filter((p: any) => p.status === ParcelStatus.DELIVERY_FAILED).length;
        const receivedCount = task.actualPickedUpParcels || 0;
        const totalDeliveryStatusCount = deliveringCount + deliveredCount + failedCount;
        const deliveryRate = totalDeliveryStatusCount > 0 ? Math.round((deliveredCount / totalDeliveryStatusCount) * 100) : 0;
        // 计算全部数量（过滤掉 TRANSFERRED_OUT）
        const allCount = parcels.filter((p: any) => p.pickupStatus !== 'TRANSFERRED_OUT').length;

        return {
            id: task.id,
            sortingCode: task.sortingCode || '',
            driverName: task.actualDriverName || task.originalDriverName || '',
            allocatedCount: task.assignedParcels || 0,
            receivedCount: receivedCount,
            deliveredCount: deliveredCount,
            deliveryRate: deliveryRate,
            deliveringCount: deliveringCount,
            failedCount: failedCount,
            allCount: allCount,
            transferInCount: task.transferInParcels || 0,
            transferOutCount: task.transferOutParcels || 0,
            parcels: parcels.map((p: any) => {
                // 查找对应的异常字典
                const exceptionDict = exceptionDictionaries.find(
                    (dict: ExceptionDictionaryType) => dict.exceptionReasonCode === p.exceptionReasonCode
                );
                
                return {
                    id: p.id,
                    trackingNumber: p.trackingNo,
                    stopNumber: formatStopNumber(p.stop, p.subStopNumber),
                    subStopNumber: p.subStopNumber || "0",
                    address: p.deliveryAddress || '',
                    status: p.status,
                    pickupStatus: p.pickupStatus,
                    routeName: p.routeName,
                    recipientName: p.recipientName,
                    recipientPhoneNumber: p.recipientPhoneNumber,
                    recipientPhoneExtension: p.recipientPhoneExtension,
                    deliverAt: p.deliverAt,
                    deliveryAttempts: p.deliveryAttempts,
                    deliveredAt: p.deliveredAt,
                    exceptionReasonCode: p.exceptionReasonCode,
                    exceptionReason: exceptionDict,
                    operatedAt: p.markExceptionAt,
                    latitude: p.latitude,
                    longitude: p.longitude
                };
            })
        } as TaskDetail;
    }, [data, exceptionDictData]);

    const [searchText, setSearchText] = useState('');
    const [activeTab, setActiveTab] = useState<ParcelStatus | 'ALL'>(() => {
        // 检查传入的activeTab是否是有效的ParcelStatus值或'ALL'
        if (activeTabFromState === 'ALL') {
            return 'ALL';
        }
        if (activeTabFromState && Object.values(ParcelStatus).includes(activeTabFromState as ParcelStatus)) {
            return activeTabFromState as ParcelStatus;
        }
        return 'ALL';
    });
    const [expanded, setExpanded] = useState(false);
    const [listHeight, setListHeight] = useState('50%'); // 列表初始高度50%
    const [isDragging, setIsDragging] = useState(false);
    const [startY, setStartY] = useState(0);
    const [currentHeight, setCurrentHeight] = useState(50); // 百分比

    // Popup 控制状态
    const [popupVisible, setPopupVisible] = useState(false);
    const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null);
    const dspParcelDetailCardRef = useRef<DspParcelDetailCardRef>(null);

    const listRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const labelRefs = useRef<(HTMLDivElement | null)[]>([]);

    // 地图引用与状态持久化
    const mapRef = useRef<any>(null);
    const skipRestoreRef = useRef<boolean>(false);
    // 首次挂载时确定是否从任务列表进入，并固定本次挂载的恢复策略
    useEffect(() => {
        const entrySource = sessionStorage.getItem('dspTaskDetailsEntrySource');
        if (entrySource === 'task-list') {
            skipRestoreRef.current = true;
            sessionStorage.removeItem('dspTaskDetailsEntrySource');
        }
    }, []);
    const saveMapState = useCallback(() => {
        if (mapRef.current) {
            try {
                const state = {
                    center: [mapRef.current.getCenter().lng, mapRef.current.getCenter().lat],
                    zoom: mapRef.current.getZoom(),
                    timestamp: Date.now()
                };
                sessionStorage.setItem('dspTaskDetailsMapState', JSON.stringify(state));
            } catch (e) {
                // ignore
            }
        }
    }, []);
    const getSavedMapState = useCallback(() => {
        // 本次挂载若决定跳过恢复，则始终返回 null，避免初始化与打点阶段不一致
        if (skipRestoreRef.current) return null;

        const saved = sessionStorage.getItem('dspTaskDetailsMapState');
        if (!saved) return null;
        try {
            const state = JSON.parse(saved);
            return state;
        } catch (e) {
            return null;
        }
    }, []);

    // 错误处理
    useEffect(() => {
        if (error) {
            Toast.show({
                content: '加载任务详情失败: ' + error.message,
                icon: 'fail'
            });
        }
    }, [error]);

    // 处理返回导航
    const handleBack = () => {
        // 设置标记，表示从派送任务相关页面跳转
        sessionStorage.setItem('fromDeliveryTask', 'true');
        
        navigate(DspPath.DELIVERY_DSP_TASK);
    };

    // 如果没有 taskId，返回首页
    useEffect(() => {
        if (!taskId) {
            Toast.show({
                content: '缺少任务ID',
                icon: 'fail'
            });
            handleBack();
        }
    }, [taskId, navigate]);

    // 过滤包裹列表
    const filteredParcels = useMemo(() => {
        if (!taskDetail) return [];

        let parcels;
        if (activeTab === 'ALL') {
            // 全部：过滤掉 TRANSFERRED_OUT 的 parcels
            parcels = taskDetail.parcels.filter((p: any) => p.pickupStatus !== 'TRANSFERRED_OUT');
        } else {
            parcels = taskDetail.parcels.filter(p => p.status === activeTab);
        }

        if (searchText) {
            parcels = parcels.filter(p =>
                p.trackingNumber.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        return parcels;
    }, [taskDetail, activeTab, searchText]);

    // 准备地图数据
    const mapAddresses = useMemo(() => {
        if (!taskDetail) return [];

        return taskDetail.parcels
            .filter(p => p.address) // 只要有地址就可以，没有经纬度会自动使用 Geocoding
            .map(p => ({
                id: p.id,
                trackingNumber: p.trackingNumber,
                address: p.address,
                deliveryTaskOrderId: parseInt(p.id), // 使用包裹ID作为deliveryTaskOrderId
                stopNumber: parseInt(p.stopNumber.split('-')[0]), // 只取主编号用于地图排序
                subStopNumber: p.subStopNumber || "0",
                latitude: p.latitude,  // 可选：有就用，没有就 Geocoding
                longitude: p.longitude, // 可选：有就用，没有就 Geocoding
                localMarkerColor: p.status === ParcelStatus.DELIVERY_FAILED ? '#EF4444' :
                    p.status === ParcelStatus.DELIVERED ? '#54ba21' : '#1890FF'
            }));
    }, [taskDetail]);

    // Tab配置
    const tabs = useMemo(() => [
        { key: 'ALL' as const, title: `${t('pages.dsp.deliveryTaskDetails.all')}(${taskDetail?.allCount || 0})` },
        { key: ParcelStatus.DELIVERY_IN_PROGRESS, title: `${t('pages.dsp.deliveryTaskDetails.delivering')}(${taskDetail?.deliveringCount || 0})` },
        { key: ParcelStatus.DELIVERED, title: `${t('pages.dsp.deliveryTaskDetails.delivered')}(${taskDetail?.deliveredCount || 0})` },
        { key: ParcelStatus.DELIVERY_FAILED, title: `${t('pages.dsp.deliveryTaskDetails.failed')}(${taskDetail?.failedCount || 0})` }
    ], [taskDetail, t]);

    // 统计区域参数
    const statsList = [
        {
            key: 'assignedParcels',
            label: t('pages.dsp.deliveryTaskDetails.assignedParcels'),
            value: taskDetail?.allocatedCount ?? '--',
        },
        {
            key: 'pickedUp',
            label: t('pages.dsp.deliveryTaskDetails.pickedUp'),
            value: taskDetail?.receivedCount ?? '--',
        },
        {
            key: 'delivered',
            label: t('pages.dsp.deliveryTaskDetails.delivered'),
            value: taskDetail?.deliveredCount ?? '--',
        },
        {
            key: 'deliverySuccessRate',
            label: t('pages.dsp.deliveryTaskDetails.deliverySuccessRate'),
            value: (taskDetail?.deliveryRate !== undefined ? `${taskDetail.deliveryRate}%` : '--'),
        },
        {
            key: 'delivering',
            label: t('pages.dsp.deliveryTaskDetails.delivering'),
            value: taskDetail?.deliveringCount ?? '--',
        },
        {
            key: 'failed',
            label: t('pages.dsp.deliveryTaskDetails.failed'),
            value: taskDetail?.failedCount ?? '--',
        },
        {
            key: 'transferOut',
            label: t('pages.dsp.deliveryTaskDetails.transferOut'),
            value: taskDetail?.transferOutCount ?? '--',
        },
        {
            key: 'transferIn',
            label: t('pages.dsp.deliveryTaskDetails.transferIn'),
            value: taskDetail?.transferInCount ?? '--',
        },
    ];
    const renderStats = expanded ? statsList : statsList.slice(0, 4);

    // 测量并统一 label 高度
    useEffect(() => {
        // 等待 DOM 更新完成
        const timeoutId = setTimeout(() => {
            const validRefs = labelRefs.current.filter(ref => ref !== null) as HTMLDivElement[];
            if (validRefs.length === 0) return;

            // 重置所有 label 的高度，让它们自适应内容
            validRefs.forEach(ref => {
                ref.style.height = 'auto';
            });

            // 等待一帧，确保内容已渲染
            requestAnimationFrame(() => {
                if (expanded && validRefs.length === 8) {
                    // 展开状态且有8个项目：前4个一组，后4个一组
                    // 前4个的最大高度
                    let maxHeightFirst = 0;
                    for (let i = 0; i < 4; i++) {
                        if (validRefs[i]) {
                            const height = validRefs[i].scrollHeight;
                            if (height > maxHeightFirst) {
                                maxHeightFirst = height;
                            }
                        }
                    }
                    // 后4个的最大高度
                    let maxHeightSecond = 0;
                    for (let i = 4; i < 8; i++) {
                        if (validRefs[i]) {
                            const height = validRefs[i].scrollHeight;
                            if (height > maxHeightSecond) {
                                maxHeightSecond = height;
                            }
                        }
                    }

                    // 分别设置前4个和后4个的高度
                    if (maxHeightFirst > 0) {
                        for (let i = 0; i < 4; i++) {
                            if (validRefs[i]) {
                                validRefs[i].style.height = `${maxHeightFirst}px`;
                            }
                        }
                    }
                    if (maxHeightSecond > 0) {
                        for (let i = 4; i < 8; i++) {
                            if (validRefs[i]) {
                                validRefs[i].style.height = `${maxHeightSecond}px`;
                            }
                        }
                    }
                } else {
                    // 未展开或少于8个：统一所有 label 的高度
                    let maxHeight = 0;
                    validRefs.forEach(ref => {
                        const height = ref.scrollHeight;
                        if (height > maxHeight) {
                            maxHeight = height;
                        }
                    });

                    if (maxHeight > 0) {
                        validRefs.forEach(ref => {
                            ref.style.height = `${maxHeight}px`;
                        });
                    }
                }
            });
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [expanded, taskDetail]);

    // 处理刷新
    const handleRefresh = async () => {
        try {
            await refetch();
        } catch (err) {
            console.error('刷新失败:', err);
        }
    };

    // 处理搜索
    const handleSearch = (value: string) => {
        setSearchText(value);
    };

    // 处理包裹点击
    const handleParcelClick = (parcelId: string) => {
        // 根据包裹ID找到对应的包裹对象
        const parcel = taskDetail?.parcels.find(p => p.id === parcelId);
        if (!parcel) {
            console.error('Parcel not found:', parcelId);
            return;
        }

        // 构建返回参数，包含当前tab状态
        const returnParams = new URLSearchParams();
        returnParams.set('taskTabType', activeTab);
        returnParams.set('taskId', taskId);

        // 跳转前保存地图视图状态
        saveMapState();

        // 根据包裹状态跳转到不同页面
        switch (parcel.status) {
            case ParcelStatus.DELIVERY_IN_PROGRESS:
                // 派送中 -> 跳转到详情页面
                navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS_DETAIL.replace(':trackingNumber', parcel.trackingNumber)}?${returnParams.toString()}`);
                break;
            case ParcelStatus.DELIVERED:
                // 已妥投 -> 跳转到POD页面
                navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS_POD.replace(':trackingNumber', parcel.trackingNumber)}?${returnParams.toString()}`);
                break;
            case ParcelStatus.DELIVERY_FAILED:
                // 派送失败 -> 跳转到标记失败页面
                if (parcel.exceptionReason) {
                    returnParams.set('exceptionReason', JSON.stringify(parcel.exceptionReason));
                }
                navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS_MARK_FAIL.replace(':trackingNumber', parcel.trackingNumber)}?${returnParams.toString()}`);
                break;
            default:
                console.warn('Unknown parcel status:', parcel.status);
                // 默认跳转到详情页面
                navigate(`${DspPath.DELIVERY_DSP_OPERATIONAL_PARTICULARS_DETAIL.replace(':trackingNumber', parcel.trackingNumber)}?${returnParams.toString()}`);
                break;
        }
    };

    // 统一关闭导航弹窗逻辑（iOS 18 增强修复）
    const handleCloseNavigationModals = useCallback(() => {
        // 方式1：通过全局事件重置所有卡片组件的状态（解决集群模式问题）
        emitCloseNavigationEvent();
        // 方式2：单个包裹模式：通过 ref 关闭
        dspParcelDetailCardRef.current?.closeLongPressModal();
        // 方式3：集群模式：直接调用工具函数关闭所有导航弹窗（确保集群模式下也能关闭，iOS 18 增强）
        closeNavigationModalsUtil();
        
        // iOS 18 需要更强制的方式关闭弹窗，使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
        requestAnimationFrame(() => {
            forceCloseNavigationModal(
                t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                t('pages.delivery.orderDetail.mapCancel') || undefined
            );
        });
    }, [t]);

    // 统一关闭弹窗逻辑
    const handleClosePopup = useCallback(() => {
        handleCloseNavigationModals();
        setPopupVisible(false);
        setSelectedParcel(null);
    }, [handleCloseNavigationModals]);

    // 处理地图标记点击事件 - 显示弹窗
    const handleMarkerClick = (data: any) => {
        // 关闭长按导航弹窗
        handleCloseNavigationModals();
        
        // 检查是否是集群数据
        if (data.isCluster && data.clusterAddresses) {
            // 将集群地址数据转换为 Parcel 格式
            const clusterParcels = data.clusterAddresses.map((addr: any) => {
                const parcel = taskDetail?.parcels.find(p => parseInt(p.id) === addr.deliveryTaskOrderId);
                if (parcel) {
                    return {
                        ...parcel,  // 以 parcel 为主（包含所有业务字段）
                        // 只覆盖地图特有的字段
                        latitude: addr.latitude,           // 地图计算的精确坐标
                        longitude: addr.longitude,         // 地图计算的精确坐标
                        deliveryTaskOrderId: addr.deliveryTaskOrderId // 地图使用的ID格式
                    };
                }
                return null;
            }).filter(Boolean);

            if (clusterParcels.length > 0) {
                setSelectedParcel({
                    ...clusterParcels[0], // 使用第一个包裹作为主要数据
                    clusterInfo: {
                        isCluster: true,
                        clusterCount: clusterParcels.length,
                        clusterAddresses: clusterParcels
                    }
                });
                setPopupVisible(true);
            }
        } else {
            // 单个包裹处理
            const parcel = taskDetail?.parcels.find(p => parseInt(p.id) === data.deliveryTaskOrderId);

            if (!parcel) {
                Toast.show({
                    content: t('pages.dsp.deliveryTaskDetails.parcelNotFound'),
                    icon: 'fail'
                });
                return;
            }
            
            setSelectedParcel(parcel);
            setPopupVisible(true);
        }
        // 强制滚动到顶部
        setTimeout(() => {
            // 方法1：查找 Popup 内的滚动容器
            const popupBody = document.querySelector('.adm-popup-body');
            if (popupBody) {
                popupBody.scrollTop = 0;
            }

            // 方法2：查找内部的滚动容器（集群模式使用 max-h-[70vh]）
            const scrollContainer = document.querySelector('.max-h-\\[70vh\\]');
            if (scrollContainer) {
                scrollContainer.scrollTop = 0;
            }

            // 方法3：直接查找所有可滚动元素
            const scrollableElements = document.querySelectorAll('*');
            scrollableElements.forEach(element => {
                if (element.scrollHeight > element.clientHeight) {
                    element.scrollTop = 0;
                }
            });
        }, 0);
    };
    
    // 处理地图点击事件 - 关闭弹窗和长按导航弹窗
    const handleMapClick = () => {
        handleClosePopup();
    };

    // 拖动处理
    const handleTouchStart = (e: React.TouchEvent) => {
        setIsDragging(true);
        setStartY(e.touches[0].clientY);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || !containerRef.current) return;

        const containerHeight = containerRef.current.clientHeight;
        const deltaY = startY - e.touches[0].clientY;
        const deltaPercent = (deltaY / containerHeight) * 100;

        let newHeight = currentHeight + deltaPercent;
        // 限制高度在20%到95%之间
        newHeight = Math.max(50, Math.min(95, newHeight));

        setCurrentHeight(newHeight);
        setListHeight(`${newHeight}%`);
        setStartY(e.touches[0].clientY);
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    // 渲染包裹卡片
    const renderParcelCard = (parcel: Parcel) => {
        return (
            <div
                key={parcel.id}
                className="bg-white rounded-lg p-4 mb-3"
                onClick={() => handleParcelClick(parcel.id)}
            >
                <div className="flex items-center justify-between">
                    {/* 左侧内容区域 */}
                    <div className="flex items-start gap-3 flex-1">
                        {/* Stop号 */}
                        <div className="flex flex-col items-center justify-center min-w-[60px]">
                            <div className="text-3xl font-bold">{parcel.stopNumber}</div>
                            <div className="text-xs text-gray-500">{t('pages.dsp.deliveryTaskDetails.stop')}</div>
                        </div>

                        {/* 包裹信息 */}
                        <div className="flex-1">
                            <div className="font-medium mb-1" style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                {parcel.trackingNumber}
                            </div>
                            <div className="text-sm text-gray-600 mb-2">{parcel.address}</div>

                            {/* 根据状态显示不同信息 */}
                            {parcel.status === ParcelStatus.DELIVERED && parcel.deliveredAt && (
                                <div className="text-sm text-gray-600">
                                    {t('pages.dsp.deliveryTaskDetails.deliveredAt')}: {new Date(parcel.deliveredAt).toLocaleString()}
                                </div>
                            )}
                            {parcel.status === ParcelStatus.DELIVERY_FAILED && (
                                <div className="space-y-1">
                                    <div className="text-sm text-gray-600">
                                        {t('pages.dsp.deliveryTaskDetails.exceptionReason')}: {parcel.exceptionReason ? getExceptionTypeText(parcel.exceptionReason) : t('pages.dsp.deliveryTaskDetails.unknown')}
                                    </div>
                                    {parcel.operatedAt && (
                                        <div className="text-sm text-gray-600">
                                            {t('pages.dsp.deliveryTaskDetails.failedAt')}: {new Date(parcel.operatedAt).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 右箭头图标 */}
                    <div className="ml-4 flex-shrink-0">
                        <RightOutline className="text-gray-400" />
                    </div>
                </div>
            </div>
        );
    };

    // 显示加载状态
    if (loading) {
        return (
            <div className="flex flex-col h-screen bg-gray-100">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        <div className="flex justify-center items-center w-full">
                            <Skeleton style={{ width: '60px', height: '20px', margin: '0 auto' }} animated />
                        </div>
                    </NavBar>
                </div>
                {/* 地图区域骨架 */}
                <div style={{ height: 'calc(50% - 46px)' }} className="bg-gray-200">
                    <Skeleton style={{width: '100%', height: '100%'}} animated/>
                </div>
                {/* 列表区域骨架 */}
                <div style={{ height: '50%' }} className="bg-gray-100 rounded-t-2xl flex flex-col">
                    {/* 拖动手柄 */}
                    <div className="w-full py-2 flex justify-center bg-white rounded-t-2xl">
                        <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
                    </div>
                    {/* 汇总信息骨架 */}
                    <div className="px-4 py-3 border-b bg-white">
                        <Skeleton.Title animated/>
                        <div className="grid grid-cols-4 gap-4 mt-2">
                            <Skeleton.Paragraph lineCount={2} animated/>
                            <Skeleton.Paragraph lineCount={2} animated/>
                            <Skeleton.Paragraph lineCount={2} animated/>
                            <Skeleton.Paragraph lineCount={2} animated/>
                        </div>
                    </div>
                    {/* 搜索栏骨架 */}
                    <div className="px-4 py-2 bg-white">
                        <Skeleton.Paragraph lineCount={1} animated/>
                    </div>
                    {/* Tab骨架 */}
                    <div className="px-4 bg-white pt-2">
                        <Skeleton.Paragraph lineCount={1} animated/>
                    </div>
                    {/* 列表项骨架 */}
                    <div className="flex-1 overflow-auto px-4 py-3 bg-gray-50">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-lg p-4 mb-3">
                                <Skeleton.Title animated/>
                                <Skeleton.Paragraph lineCount={2} animated/>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // 如果没有数据
    if (!taskDetail) {
        return (
            <div className="flex flex-col h-screen bg-gray-100">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>{t('pages.dsp.deliveryTaskDetails.title')}</NavBar>
                </div>
                <div className="flex items-center justify-center flex-1">
                    <div className="text-gray-500">{t('pages.dsp.deliveryTaskDetails.noData')}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-gray-100" ref={containerRef}>
            {/* 顶部导航栏 */}
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>
                    {taskDetail.id}
                </NavBar>
            </div>

            {/* 地图区域 */}
            <div style={{ height: `calc(100% - ${listHeight} - 46px)` }} className="relative z-0">
                {mapAddresses.length > 0 ? (
                    <MapboxMap
                        deliveryAddresses={mapAddresses}
                        onMarkerClick={handleMarkerClick}
                        onMapClick={handleMapClick}
                        showCurrentLocation={false}
                        mapRef={mapRef}
                        getSavedMapState={getSavedMapState}
                        pageMarker={MapBoxPageMarker.DSP_TASK_DETAIL}
                    />
                ) : (
                    <div className="flex items-center justify-center h-full bg-gray-200">
                        <div className="text-gray-500">{t('pages.dsp.deliveryTaskDetails.noMapData')}</div>
                    </div>
                )}
            </div>

            {/* 可拖动的列表区域 */}
            <div
                ref={listRef}
                style={{ height: listHeight }}
                className="bg-gray-100 rounded-t-2xl shadow-lg flex flex-col relative z-50"
            >
                {/* 拖动手柄 */}
                <div
                    className="w-full"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {/*定义手柄容器和交互区*/}
                    <div
                        className="py-2 flex justify-center cursor-move bg-white rounded-t-2xl"
                    >
                        {/*仅做手柄装饰*/}
                        <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
                    </div>

                    {/* 包裹汇总信息 */}
                    <div className="px-4 py-3 border-b bg-white">
                        {/* 头部信息 */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <DriverSvg width={16} height={16} />
                                <span className="font-medium">{taskDetail.driverName}</span>
                            </div>
                            <span className="text-gray-500 text-sm">{taskDetail.sortingCode}</span>
                        </div>

                        {/* 默认显示的基本信息 */}
                        <div className={`grid grid-cols-4 gap-4 mb-2`}>
                            {renderStats.map((item, index) => {
                                // 确保 refs 数组有足够长度
                                if (!labelRefs.current[index]) {
                                    labelRefs.current[index] = null;
                                }
                                return (
                                    <div className="text-center flex flex-col" key={item.key}>
                                        <div
                                            ref={(el) => {
                                                labelRefs.current[index] = el;
                                            }}
                                            className="text-gray-500 text-xs mb-1 overflow-hidden text-center leading-relaxed"
                                        >
                                            {item.label}
                                        </div>
                                        <div className="text-2xl font-bold">{item.value}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <button
                            type="button"
                            className="w-full flex items-center justify-center text-gray-500 text-xs mb-2 focus:outline-none"
                            onClick={() => setExpanded(v => !v)}
                        >
                            {expanded
                                ? <UpOutline className="mr-1" />
                                : <DownOutline className="mr-1" />
                            }
                        </button>
                    </div>
                </div>

                {/* 搜索栏 */}
                <DspSearchBar onSearch={handleSearch}/>
                {/* 扫码组件 */}
                {isOnline && <BarCodeScanner showManualInputIcon={false}/>}

                {/* Tab分栏 */}
                <div className="px-4 bg-white mt-2 pt-2">
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key as ParcelStatus | 'ALL')}
                    >
                        {tabs.map(tab => (
                            <Tabs.Tab key={tab.key} title={tab.title} />
                        ))}
                    </Tabs>
                </div>

                {/* 包裹列表 */}
                <div className="flex-1 overflow-auto px-4 py-3 bg-gray-50">
                    <PullToRefreshWrapper onRefresh={handleRefresh}>
                        {filteredParcels.length > 0 ? (
                            filteredParcels.map(renderParcelCard)
                        ) : (
                            <div className="text-center text-gray-400 py-10">
                                {t('pages.dsp.deliveryTaskDetails.noParcelData')}
                            </div>
                        )}
                    </PullToRefreshWrapper>
                </div>
            </div>
            
            {/* Popup 弹窗 */}
            <Popup
                visible={popupVisible}
                position="bottom"
                mask={false}
                bodyStyle={{
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    maxHeight: '80vh'
                }}
                onClose={handleClosePopup}
            >
                {selectedParcel && (
                    <DspParcelDetailCard
                        ref={dspParcelDetailCardRef}
                        parcel={selectedParcel}
                        onClose={handleClosePopup}
                    />
                )}
            </Popup>
        </div>
    );
};

export default DspDeliveryTaskDetailsPage;

